// Player de quiz: orquesta start → preguntas una a una → finish → resultados.
// El scoring lo hace el backend al guardar cada respuesta; aquí solo guardamos la última
// versión de cada respuesta y al finalizar mostramos correct/incorrect + soluciones.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { Header } from '@/components/organisms/Header';
import {
  ClozeQuestion,
  MatrixSortQuestion,
  MultipleQuestion,
  SingleQuestion,
  SortQuestion,
} from '@/components/molecules/QuizQuestion';
import { ApiError } from '@/services/api';
import {
  finishAttempt,
  getAttempt,
  getQuizForPlayer,
  startAttempt,
  submitAnswer,
  type AttemptDetail,
  type MatrixMatch,
  type PlayerClozePayload,
  type PlayerMatrixSortPayload,
  type PlayerQuestion,
  type PlayerSinglePayload,
  type PlayerSortPayload,
  type QuizPlayer,
} from '@/services/quizzes';
import { safeHtml } from '@/utils/safeHtml';
import styles from './Quiz.module.scss';

type AnswerValue =
  | { kind: 'single'; selected_id: string | null }
  | { kind: 'multiple'; selected_ids: string[] }
  | { kind: 'sort'; ordered_ids: string[] }
  | { kind: 'matrix'; matches: MatrixMatch[] }
  | { kind: 'cloze'; values: string[] };

function emptyAnswer(q: PlayerQuestion): AnswerValue {
  switch (q.answer_type) {
    case 'single': return { kind: 'single', selected_id: null };
    case 'multiple': return { kind: 'multiple', selected_ids: [] };
    case 'sort_answer': {
      const p = q.payload as PlayerSortPayload;
      return { kind: 'sort', ordered_ids: p.items.map((i) => i.id) };
    }
    case 'matrix_sort_answer': return { kind: 'matrix', matches: [] };
    case 'cloze_answer': {
      const p = q.payload as PlayerClozePayload;
      const count = p.blank_count ?? p.blanks?.length ?? 0;
      return { kind: 'cloze', values: Array.from({ length: count }, () => '') };
    }
  }
}

export function Quiz() {
  const { id: idStr } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const lessonIdParam = searchParams.get('lesson');
  const navigate = useNavigate();

  const quizId = idStr ? Number(idStr) : NaN;
  const lessonId = lessonIdParam ? Number(lessonIdParam) : null;

  const [player, setPlayer] = useState<QuizPlayer | null>(null);
  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Record<number, AnswerValue>>({});
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<AttemptDetail | null>(null);

  // 1) Cargar quiz + crear attempt al montar.
  useEffect(() => {
    let cancelled = false;
    if (!Number.isInteger(quizId) || quizId < 1) {
      setError('Quiz inválido');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const p = await getQuizForPlayer(quizId);
        if (cancelled) return;
        if (p.questions.length === 0) {
          setError('Este quiz aún no tiene preguntas.');
          setPlayer(p);
          return;
        }
        const aId = await startAttempt(quizId, lessonId ?? undefined);
        if (cancelled) return;
        setPlayer(p);
        setAttemptId(aId);
        const initial: Record<number, AnswerValue> = {};
        for (const q of p.questions) initial[q.id] = emptyAnswer(q);
        setAnswers(initial);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [quizId, lessonId]);

  const total = player?.questions.length ?? 0;
  const question = player?.questions[current];

  const setAnswerFor = useCallback((qid: number, v: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [qid]: v }));
  }, []);

  const persistCurrent = useCallback(async () => {
    if (!question || !attemptId) return;
    const v = answers[question.id];
    if (!v) return;
    let data: Record<string, unknown>;
    switch (v.kind) {
      case 'single':
        if (!v.selected_id) return; // nada que enviar
        data = { selected_id: v.selected_id };
        break;
      case 'multiple': data = { selected_ids: v.selected_ids }; break;
      case 'sort': data = { ordered_ids: v.ordered_ids }; break;
      case 'matrix': data = { matches: v.matches }; break;
      case 'cloze': data = { values: v.values }; break;
    }
    await submitAnswer(attemptId, {
      question_id: question.id,
      answer_type: question.answer_type,
      data: data as never,
    });
  }, [question, attemptId, answers]);

  const goNext = useCallback(async () => {
    if (!player) return;
    setSubmitting(true);
    try {
      await persistCurrent();
      setCurrent((c) => Math.min(c + 1, player.questions.length - 1));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setSubmitting(false);
    }
  }, [player, persistCurrent]);

  const goPrev = useCallback(async () => {
    setSubmitting(true);
    try {
      await persistCurrent();
      setCurrent((c) => Math.max(c - 1, 0));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setSubmitting(false);
    }
  }, [persistCurrent]);

  const onFinish = useCallback(async () => {
    if (!attemptId) return;
    setSubmitting(true);
    try {
      await persistCurrent();
      await finishAttempt(attemptId);
      const detail = await getAttempt(attemptId);
      setResults(detail);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setSubmitting(false);
    }
  }, [attemptId, persistCurrent]);

  const backHref = lessonId ? `/lecciones/leccion/${lessonId}` : '/lecciones';

  if (loading) {
    return (
      <>
        <Header title="Quiz" />
        <main className={styles.main}><div className={styles.center}><Spinner size="lg" /></div></main>
      </>
    );
  }
  if (error) {
    return (
      <>
        <Header title="Quiz" />
        <main className={styles.main}>
          <Link to={backHref} className={styles.backLink}>
            <Icon name="chevron-left" size={16} /> Volver
          </Link>
          <p className={styles.error}>{error}</p>
        </main>
      </>
    );
  }
  if (!player) return null;

  if (results) {
    return <QuizResults results={results} backHref={backHref} onRetry={() => navigate(0)} />;
  }

  if (!question) return null;
  const answer = answers[question.id];
  const isLast = current === total - 1;
  const progressPct = Math.round(((current + 1) / total) * 100);

  return (
    <>
      <Header title={player.quiz.title} />
      <main className={styles.main}>
        <Link to={backHref} className={styles.backLink}>
          <Icon name="chevron-left" size={16} /> Volver
        </Link>

        <div className={styles.progress}>
          <span className={styles.progressLabel}>Pregunta {current + 1} / {total}</span>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
          </div>
          <span className={styles.progressLabel}>{progressPct}%</span>
        </div>

        <article className={styles.questionCard}>
          <header className={styles.questionHeader}>
            <span className={styles.questionTag}>{labelForType(question.answer_type)} · {question.points} pt{question.points > 1 ? 's' : ''}</span>
            <h2 className={styles.questionText} dangerouslySetInnerHTML={{ __html: safeHtml(question.question_text) }} />
          </header>

          <QuestionBody
            question={question}
            answer={answer}
            onChange={(next) => setAnswerFor(question.id, next)}
            disabled={submitting}
          />

          <div className={styles.nav}>
            <Button variant="secondary" onClick={goPrev} disabled={submitting || current === 0}>
              <Icon name="chevron-left" size={14} /> Anterior
            </Button>
            {isLast ? (
              <Button variant="primary" onClick={onFinish} disabled={submitting}>
                {submitting ? 'Enviando…' : 'Finalizar quiz'}
              </Button>
            ) : (
              <Button variant="primary" onClick={goNext} disabled={submitting}>
                Siguiente <Icon name="chevron-right" size={14} />
              </Button>
            )}
          </div>
        </article>
      </main>
    </>
  );
}

interface QuestionBodyProps {
  question: PlayerQuestion;
  answer: AnswerValue | undefined;
  onChange: (v: AnswerValue) => void;
  disabled: boolean;
}

function QuestionBody({ question, answer, onChange, disabled }: QuestionBodyProps) {
  if (!question.payload || !answer) return null;
  switch (question.answer_type) {
    case 'single':
      return (
        <SingleQuestion
          payload={question.payload as PlayerSinglePayload}
          value={answer.kind === 'single' ? answer.selected_id : null}
          onChange={(id) => onChange({ kind: 'single', selected_id: id })}
          disabled={disabled}
        />
      );
    case 'multiple':
      return (
        <MultipleQuestion
          payload={question.payload as PlayerSinglePayload}
          value={answer.kind === 'multiple' ? answer.selected_ids : []}
          onChange={(ids) => onChange({ kind: 'multiple', selected_ids: ids })}
          disabled={disabled}
        />
      );
    case 'sort_answer':
      return (
        <SortQuestion
          payload={question.payload as PlayerSortPayload}
          value={answer.kind === 'sort' ? answer.ordered_ids : []}
          onChange={(ids) => onChange({ kind: 'sort', ordered_ids: ids })}
          disabled={disabled}
        />
      );
    case 'matrix_sort_answer':
      return (
        <MatrixSortQuestion
          payload={question.payload as PlayerMatrixSortPayload}
          value={answer.kind === 'matrix' ? answer.matches : []}
          onChange={(matches) => onChange({ kind: 'matrix', matches })}
          disabled={disabled}
        />
      );
    case 'cloze_answer':
      return (
        <ClozeQuestion
          payload={question.payload as PlayerClozePayload}
          value={answer.kind === 'cloze' ? answer.values : []}
          onChange={(values) => onChange({ kind: 'cloze', values })}
          disabled={disabled}
        />
      );
  }
}

function labelForType(t: PlayerQuestion['answer_type']): string {
  switch (t) {
    case 'single': return 'Elige 1';
    case 'multiple': return 'Elige varias';
    case 'sort_answer': return 'Ordena';
    case 'matrix_sort_answer': return 'Empareja';
    case 'cloze_answer': return 'Completa los huecos';
  }
}

interface QuizResultsProps {
  results: AttemptDetail;
  backHref: string;
  onRetry: () => void;
}

function QuizResults({ results, backHref, onRetry }: QuizResultsProps) {
  const { attempt, answers, questions } = results;
  const byQ = useMemo(() => {
    const m = new Map<number, typeof answers[number]>();
    for (const a of answers) m.set(a.question_id, a);
    return m;
  }, [answers]);

  return (
    <>
      <Header title="Resultados" />
      <main className={styles.main}>
        <Link to={backHref} className={styles.backLink}>
          <Icon name="chevron-left" size={16} /> Volver a la lección
        </Link>

        <div className={styles.results}>
          <div className={styles.scoreBox}>
            <div className={styles.scoreNumber}>{attempt.score} / {attempt.max_score}</div>
            <div className={styles.scoreLabel}>
              {attempt.max_score > 0
                ? `${Math.round((attempt.score / attempt.max_score) * 100)}% de aciertos`
                : 'Sin puntos disponibles'}
            </div>
          </div>

          <div className={styles.resultsList}>
            {questions.map((q) => {
              const a = byQ.get(q.id);
              const correct = a?.is_correct === 1;
              return (
                <div key={q.id} className={styles.resultItem}>
                  <div className={styles.resultItemHeader}>
                    <h4>
                      <span>{q.position + 1}. </span>
                      <span dangerouslySetInnerHTML={{ __html: safeHtml(q.question_text) }} />
                    </h4>
                    <Badge tone={correct ? 'success' : 'warning'}>
                      {correct ? 'Correcto' : 'Incorrecto'}
                    </Badge>
                  </div>
                  <ResultRender q={q} answer={a} />
                </div>
              );
            })}
          </div>

          <div className={styles.nav}>
            <Button variant="secondary" asChild>
              <Link to={backHref}>Volver</Link>
            </Button>
            <Button variant="primary" onClick={onRetry}>Repetir quiz</Button>
          </div>
        </div>
      </main>
    </>
  );
}

interface ResultRenderProps {
  q: PlayerQuestion;
  answer: AttemptDetail['answers'][number] | undefined;
}

function ResultRender({ q, answer }: ResultRenderProps) {
  if (!q.payload) return null;
  const payload = answer?.answer_payload as Record<string, unknown> | undefined;

  switch (q.answer_type) {
    case 'single':
      return (
        <SingleQuestion
          payload={q.payload as PlayerSinglePayload}
          value={null}
          onChange={() => {}}
          disabled
          reveal={{ selectedId: (payload?.['selected_id'] as string) ?? null }}
        />
      );
    case 'multiple':
      return (
        <MultipleQuestion
          payload={q.payload as PlayerSinglePayload}
          value={[]}
          onChange={() => {}}
          disabled
          reveal={{ selectedIds: (payload?.['selected_ids'] as string[]) ?? [] }}
        />
      );
    case 'sort_answer':
      return (
        <SortQuestion
          payload={q.payload as PlayerSortPayload}
          value={(payload?.['ordered_ids'] as string[]) ?? []}
          onChange={() => {}}
          disabled
          reveal
        />
      );
    case 'matrix_sort_answer':
      return (
        <MatrixSortQuestion
          payload={q.payload as PlayerMatrixSortPayload}
          value={(payload?.['matches'] as MatrixMatch[]) ?? []}
          onChange={() => {}}
          disabled
          reveal
        />
      );
    case 'cloze_answer':
      return (
        <ClozeQuestion
          payload={q.payload as PlayerClozePayload}
          value={(payload?.['values'] as string[]) ?? []}
          onChange={() => {}}
          disabled
          reveal
        />
      );
  }
}
